import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingOverlay from '../overlays/LoadingOverlay';
import ErrorModal from '../modals/ErrorModal';
import SuccessModal from '../modals/SuccessModal';
import { detectChangesForEntity, createNoChangesModalData, logChangeDetection } from '../../utils/changeDetection';
import '../../pages/Profile.css';

const UserProfile = ({ canEditName = false, canUpdatePassword = false }) => {
  const { user, token, changePassword, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [originalUserData, setOriginalUserData] = useState(null);

  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (activeTab === 'password' && !canUpdatePassword) {
      setActiveTab('profile');
    } else if (activeTab === 'profile' && !canEditName && canUpdatePassword) {
      setActiveTab('password');
    }
  }, [activeTab, canEditName, canUpdatePassword]);

  useEffect(() => {
    const fetchOriginalUserData = async () => {
      if (token) {
        try {
          const response = await fetch('/api/auth/profile', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          const profileResponse = await response.json();

          if (profileResponse.success) {
            setOriginalUserData(profileResponse.data.user);
          }
        } catch (error) {
          console.error('Failed to load original user data:', error);
        }
      }
    };

    fetchOriginalUserData();
  }, [token]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    setShowErrorModal(false);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setShowErrorModal(false);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!canEditName) return;
    setLoading(true);
    setShowErrorModal(false);
    setShowSuccessModal(false);

    if (!originalUserData) {
      setModalData({
        title: 'Data Not Loaded',
        message: 'User data not loaded. Please refresh the page and try again.',
        details: 'The profile data could not be loaded properly.'
      });
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    const changeResult = detectChangesForEntity('profile', profileData, originalUserData);
    logChangeDetection('Profile update', changeResult, originalUserData, profileData);

    if (!changeResult.hasChanges) {
      setModalData(createNoChangesModalData('profile'));
      setShowSuccessModal(true);
      setLoading(false);
      return;
    }

    try {
      const updateResponse = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          version: originalUserData.updatedAt
        })
      });

      const updateResult = await updateResponse.json();

      if (updateResult.success) {
        setModalData({
          title: 'Profile Updated',
          message: 'Your profile has been updated.',
          details: {
            Name: `${updateResult.data.user.firstName} ${updateResult.data.user.lastName}`,
            Email: updateResult.data.user.email,
            'Updated at': new Date(updateResult.data.user.updatedAt).toLocaleString()
          }
        });
        setShowSuccessModal(true);
        setOriginalUserData(updateResult.data.user);
      } else {
        if (updateResult.code === 'VERSION_CONFLICT') {
          setModalData({
            title: 'Version Conflict',
            message: updateResult.message,
            details: 'The profile was modified elsewhere. Refresh and try again.'
          });
        } else {
          setModalData({
            title: 'Update Failed',
            message: updateResult.message || 'Unable to update the profile.',
            details: 'Check your input and try again.'
          });
        }
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setModalData({
        title: 'Connection Error',
        message: 'Failed to connect to the server.',
        details: error.message
      });
      setShowErrorModal(true);
    }

    setLoading(false);
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (!canUpdatePassword) return;
    setLoading(true);
    setShowErrorModal(false);
    setShowSuccessModal(false);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setModalData({
        title: 'Password Mismatch',
        message: 'New passwords do not match.',
        details: 'Make sure both password fields match.'
      });
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);

    if (result.success) {
      setModalData({
        title: 'Password Changed',
        message: 'Your password has been updated.',
        details: 'Use your new password next time you sign in.'
      });
      setShowSuccessModal(true);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } else {
      if (result.code === 'VERSION_CONFLICT') {
        setModalData({
          title: 'Version Conflict',
          message: result.message,
          details: 'Refresh the page and try again.'
        });
        setShowErrorModal(true);
      } else {
        setModalData({
          title: 'Password Change Failed',
          message: result.message || 'Unable to change password.',
          details: 'Check your current password and try again.'
        });
        setShowErrorModal(true);
      }
    }

    setLoading(false);
  };

  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.trim() || '?';

  return (
    <>
      <LoadingOverlay isVisible={loading} message="Saving…" />
      <div className="profile-card">
        <header className="profile-header">
          <p className="profile-title">Your account</p>
          <div className="profile-user">
            <div className="profile-avatar">
              {user?.avatar ? (
                <img src={user.avatar} alt="" />
              ) : (
                initials
              )}
            </div>
            <div>
              <h2 className="profile-user-name">
                {user?.firstName} {user?.lastName}
              </h2>
              <p className="profile-user-email">{user?.email}</p>
              {user?.createdAt && (
                <p className="profile-user-meta">
                  Member since {new Date(user.createdAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </header>

        {(canEditName || canUpdatePassword) && (
          <div className="profile-tabs" role="tablist">
            {canEditName && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'profile'}
                className={`profile-tab${activeTab === 'profile' ? ' profile-tab--active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                Profile
              </button>
            )}
            {canUpdatePassword && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'password'}
                className={`profile-tab${activeTab === 'password' ? ' profile-tab--active' : ''}`}
                onClick={() => setActiveTab('password')}
              >
                Password
              </button>
            )}
          </div>
        )}

        {activeTab === 'profile' && canEditName && (
          <form onSubmit={handleProfileUpdate} className="profile-form">
            <input
              type="text"
              name="firstName"
              placeholder="First name"
              value={profileData.firstName}
              onChange={handleProfileChange}
              required
              className="profile-input"
              autoComplete="given-name"
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last name"
              value={profileData.lastName}
              onChange={handleProfileChange}
              required
              className="profile-input"
              autoComplete="family-name"
            />
            <button type="submit" disabled={loading} className="profile-btn profile-btn--primary">
              {loading ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        )}

        {activeTab === 'password' && canUpdatePassword && (
          <form onSubmit={handlePasswordUpdate} className="profile-form">
            <input
              type="password"
              name="currentPassword"
              placeholder="Current password"
              value={passwordData.currentPassword}
              onChange={handlePasswordChange}
              required
              className="profile-input"
              autoComplete="current-password"
            />
            <input
              type="password"
              name="newPassword"
              placeholder="New password"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              required
              className="profile-input"
              autoComplete="new-password"
            />
            <input
              type="password"
              name="confirmPassword"
              placeholder="Confirm new password"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              required
              className="profile-input"
              autoComplete="new-password"
            />
            <button type="submit" disabled={loading} className="profile-btn profile-btn--primary">
              {loading ? 'Saving…' : 'Update password'}
            </button>
          </form>
        )}

        <footer className="profile-footer">
          <button type="button" onClick={logout} className="profile-btn profile-btn--logout">
            Sign out
          </button>
        </footer>
      </div>

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={modalData?.title}
        message={modalData?.message}
        details={modalData?.details}
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={modalData?.title}
        message={modalData?.message}
        details={modalData?.details}
      />
    </>
  );
};

export default UserProfile;
